import { lazy, type ReactNode } from "react";
import type { RouteObject } from "react-router-dom";
import UnderDevelopment from "../../features/shared/pages/UnderDevelopment";
import { APP_ROUTES } from "../routePaths";
import { createProtectedIndexRoute, withSuspense } from "./helpers";

const Customer = lazy(() => import("../../features/customer/pages/Customer"));
const CustomerList = lazy(() => import("../../features/customer/pages/CustomerList"));
const EndCustomerPage = lazy(() => import("../../features/end-customer/pages/EndCustomerPage"));
const Supplier = lazy(() => import("../../features/Supplier/pages/Supplier"));
const SupplierList = lazy(() => import("../../features/Supplier/pages/SupplierList"));
const AgentMasterCompany = lazy(
  () => import("../../features/agent-master/pages/AgentmasterCompanyadd")
);
const AgentMaster = lazy(() => import("../../features/agent-master/pages/AgentMaster"));
const AddressMaster = lazy(() => import("../../features/Address/AddressMaster"));
const Employee = lazy(() => import("../../features/employee-master/pages/Employee"));
const EmployeeMasterView = lazy(() => import("../../features/employee-master/pages/EmployeeMasterView"));
const TruckMasterView = lazy(() => import("../../features/truck-master/pages/TruckMasterView"));
const TruckMasterFormPage = lazy(() => import("../../features/truck-master/pages/TruckMasterFormPage"));
const TruckOrderCalendarPage = lazy(
  () => import("../../features/truck-order/pages/TruckOrderCalendarPage")
);
const TruckLocationBoardPage = lazy(
  () => import("../../features/truck-location/pages/TruckLocationBoardPage")
);
const TruckDriverDetailsPage = lazy(
  () => import("../../features/truck-driver-details/pages/TruckDriverDetailsPage")
);
const TruckMaintenanceView = lazy(() => import("../../features/truck-maintenance/pages/TruckMaintenanceView"));
const TruckMaintenanceDashboard = lazy(
  () => import("../../features/truck-maintenance-dashboard/pages/TruckMaintenanceDashboard")
);
const DriverMasterView = lazy(() => import("../../features/driver-master/pages/DriverMasterView"));
const DriverMasterFormPage = lazy(() => import("../../features/driver-master/pages/DriverMasterFormPage"));
const ChartOfAccountsPage = lazy(
  () => import("../../features/chart-of-accounts/pages/ChartOfAccountsPage")
);
const ExpenseMasterPage = lazy(() => import("../../features/expense-master/pages/ExpenseMasterPage"));
const PendingPaymentsPage = lazy(() => import("../../features/pending-payments/pages/PendingPaymentsPage"));
const PayablesAgeingPage = lazy(() => import("../../features/payable-reports/pages/PayablesAgeingPage"));
const VendorOutstandingPage = lazy(() => import("../../features/payable-reports/pages/VendorOutstandingPage"));
const HirePurchaseReportPage = lazy(() => import("../../features/payable-reports/pages/HirePurchaseReportPage"));
const PaymentRegisterPage = lazy(() => import("../../features/payable-reports/pages/PaymentRegisterPage"));
const PayableExceptionsPage = lazy(() => import("../../features/payable-reports/pages/PayableExceptionsPage"));
const SubExpensePage = lazy(() => import("../../features/sub-expense/pages/SubExpensePage"));
const SubExpenseContractsPage = lazy(() => import("../../features/sub-expense-contracts/pages/SubExpenseContractsPage"));
const PeriodLockPage = lazy(() => import("../../features/accounts-controls/pages/PeriodLockPage"));
const InventoryListPage = lazy(() => import("../../features/inventory/pages/InventoryListPage"));
const InventoryFormPage = lazy(() => import("../../features/inventory/pages/InventoryFormPage"));
const InventoryDetailPage = lazy(() => import("../../features/inventory/pages/InventoryDetailPage"));
const ReconListPage = lazy(() => import("../../features/recon/pages/ReconListPage"));
const LocationMasterPage = lazy(() => import("../../features/location-master/pages/LocationMasterPage"));
const UomMasterPage = lazy(() => import("../../features/uom/pages/UomMasterPage"));
const ProductMasterPage = lazy(() => import("../../features/product/pages/ProductMasterPage"));
const ReconDetailPage = lazy(() => import("../../features/recon/pages/ReconDetailPage"));
const VesselMasterPage = lazy(() => import("../../features/vessel-master/pages/VesselMasterPage"));

interface RouteConfig {
  path: string;
  element: ReactNode;
}

const salesMasterRouteConfigs: RouteConfig[] = [
  { path: "/CompanySetting", element: <UnderDevelopment /> },
  { path: "/AgentCompanyMaster", element: withSuspense(<AgentMasterCompany />) },
  { path: "/AgentMaster", element: withSuspense(<AgentMaster />) },
  { path: "/AddressMaster", element: withSuspense(<AddressMaster />) },
  { path: "/PaymentTermsMaster", element: <UnderDevelopment /> },
  { path: "/SymbolMaster", element: <UnderDevelopment /> },
  { path: "/JobTypeMaster", element: <UnderDevelopment /> },
  { path: "/JobTypeDetails", element: <UnderDevelopment /> },
  { path: "/JobStatusMaster", element: <UnderDevelopment /> },
  { path: "/JobStatusDetails", element: <UnderDevelopment /> },
  { path: "/ItemMaster", element: <UnderDevelopment /> },
  { path: "/ProductMaster", element: withSuspense(<ProductMasterPage />) },
  { path: "/TaxMaster", element: <UnderDevelopment /> },
  { path: "/UOMMaster", element: withSuspense(<UomMasterPage />) },
  // Customer master, on the .NET paths: /CustomerMaster is the add form,
  // /CustomerMaster/CustomerView the list (AllCustomer.js). The static
  // "CustomerView" segment ranks above the ":id" pattern of the edit route.
  { path: APP_ROUTES.customerMaster, element: withSuspense(<Customer />) },
  { path: APP_ROUTES.customerMasterView, element: withSuspense(<CustomerList />) },
  // The same form with one customer loaded. Id in the path, not a query
  // string: the router shim re-serialises search values.
  { path: APP_ROUTES.customerMasterEdit(), element: withSuspense(<Customer />) },
  { path: APP_ROUTES.endCustomerMaster, element: withSuspense(<EndCustomerPage />) },
  { path: "/paymentReceiptInfo", element: <UnderDevelopment /> },
  { path: "/CustomerQuotation", element: <UnderDevelopment /> },
  { path: "/SupplierMaster", element: withSuspense(<Supplier />) },
  // Id in the path, as for customers: the router shim re-serialises ?Id=.
  { path: "/SupplierMaster/edit/:id", element: withSuspense(<Supplier />) },
  // The legacy VIEW button's path, kept so bookmarks land on the list.
  { path: "/SupplierMaster/SupplierView", element: withSuspense(<SupplierList />) },
  { path: APP_ROUTES.employeeMaster, element: withSuspense(<Employee />) },
  { path: APP_ROUTES.employeeMasterView, element: withSuspense(<EmployeeMasterView />) },
  // /DriverMaster is the add / edit form (?Id=123 edits), matching the legacy
  // screen; the list lives at /DriverMaster/DriverView.
  { path: "/DriverMaster", element: withSuspense(<DriverMasterFormPage />) },
  { path: "/DriverMaster/DriverView", element: withSuspense(<DriverMasterView />) },
  { path: "/TruckMaster", element: withSuspense(<TruckMasterFormPage />) },
  { path: "/TruckMaster/TruckView", element: withSuspense(<TruckMasterView />) },
  // The order calendar. Path kept from the legacy screen: the employee
  // dashboard tile and the transport dashboard both already link to it.
  { path: APP_ROUTES.truckOrderCalendar, element: withSuspense(<TruckOrderCalendarPage />) },
  { path: APP_ROUTES.truckLocationBoard, element: withSuspense(<TruckLocationBoardPage />) },
  { path: APP_ROUTES.truckDriverDetails, element: withSuspense(<TruckDriverDetailsPage />) },
  { path: "/truck-maintenance", element: withSuspense(<TruckMaintenanceView />) },
  { path: "/truck-maintenance-dashboard", element: withSuspense(<TruckMaintenanceDashboard />) },
  { path: APP_ROUTES.locationMaster, element: withSuspense(<LocationMasterPage />) },
  { path: "/PortMaster", element: <UnderDevelopment /> },
  // Vessel names the company uses: rename and merge spellings (change vessel-master-and-ptp-schedule).
  { path: APP_ROUTES.vesselMaster, element: withSuspense(<VesselMasterPage />) },
  { path: "/PaymentReceiptInfo", element: <UnderDevelopment /> },
  // Workshop inventory. React Router ranks the static "new" segment above the
  // ":id" pattern, so /Inventory/new resolves to the form, not the detail page.
  { path: APP_ROUTES.inventory, element: withSuspense(<InventoryListPage />) },
  { path: APP_ROUTES.inventoryNew, element: withSuspense(<InventoryFormPage />) },
  { path: APP_ROUTES.inventoryDetail(), element: withSuspense(<InventoryDetailPage />) },
  { path: APP_ROUTES.inventoryEdit(), element: withSuspense(<InventoryFormPage />) },

  // Reconditioning. A recon job is created by removing a unit from a truck on
  // the item detail screen, so there is no "new" route here - a job without a
  // removal behind it would have nothing to record.
  { path: APP_ROUTES.recon, element: withSuspense(<ReconListPage />) },
  { path: APP_ROUTES.reconDetail(), element: withSuspense(<ReconDetailPage />) },
];

const accountMasterRoutePaths = [
  "/BankMaster",
] as const;

export const salesMasterRoutes: RouteObject[] = salesMasterRouteConfigs.map(({ path, element }) =>
  createProtectedIndexRoute(path, element)
);

export const accountsMasterRoutes: RouteObject[] = [
  ...accountMasterRoutePaths.map((path) =>
    createProtectedIndexRoute(path, <UnderDevelopment />)
  ),
  // The QNE chart of accounts. Keeps the legacy path so existing menu entries
  // and bookmarks land on the React screen.
  createProtectedIndexRoute("/AccountsGroupMaster", withSuspense(<ChartOfAccountsPage />)),
  // Expense Master. Keeps the legacy path, which is what the sidebar's
  // Accounts > Expense Master entry (data/menuConfig.ts) already points at.
  createProtectedIndexRoute("/ExpenseMaster", withSuspense(<ExpenseMasterPage />)),
  // Pending Payments - the monthly bills of the sub expenses. Legacy path kept.
  createProtectedIndexRoute("/PaymentVoucher/PaymentPending", withSuspense(<PendingPaymentsPage />)),
  createProtectedIndexRoute("/Accounts/PeriodLock", withSuspense(<PeriodLockPage />)),
  createProtectedIndexRoute("/SubExpenseMaster", withSuspense(<SubExpensePage />)),
  createProtectedIndexRoute("/Accounts/SubExpenseContracts", withSuspense(<SubExpenseContractsPage />)),
  createProtectedIndexRoute("/Accounts/Reports/PayablesAgeing", withSuspense(<PayablesAgeingPage />)),
  createProtectedIndexRoute("/Accounts/Reports/VendorOutstanding", withSuspense(<VendorOutstandingPage />)),
  createProtectedIndexRoute("/Accounts/Reports/HirePurchase", withSuspense(<HirePurchaseReportPage />)),
  createProtectedIndexRoute("/Accounts/Reports/PaymentRegister", withSuspense(<PaymentRegisterPage />)),
  createProtectedIndexRoute("/Accounts/Reports/PayableExceptions", withSuspense(<PayableExceptionsPage />)),
];
